import { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

interface Canned {
  id: string;
  title: string;
  shortcut: string;
  body: string;
}

const SavedReplies = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const auth = token ? { Authorization: `Bearer ${token}` } : undefined;

  const [items, setItems] = useState<Canned[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', shortcut: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const load = () =>
    fetch(`${API_BASE}/canned-responses`, { headers: auth })
      .then(r => (r.ok ? r.json() : { canned: [] }))
      .then(d => setItems(d.canned || []))
      .catch(() => undefined);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const reset = () => {
    setEditingId(null);
    setForm({ title: '', shortcut: '', body: '' });
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setToast({ msg: 'Title and message are required.', ok: false });
      return;
    }
    setSaving(true);
    try {
      const url = editingId
        ? `${API_BASE}/canned-responses/${editingId}`
        : `${API_BASE}/canned-responses`;
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth || {}) },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        await load();
        reset();
        setToast({ msg: 'Saved reply stored.', ok: true });
      } else {
        setToast({ msg: 'Could not save.', ok: false });
      }
    } catch {
      setToast({ msg: 'Could not save.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  const edit = (c: Canned) => {
    setEditingId(c.id);
    setForm({ title: c.title, shortcut: c.shortcut, body: c.body });
  };

  const remove = async (c: Canned) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    await fetch(`${API_BASE}/canned-responses/${c.id}`, { method: 'DELETE', headers: auth });
    if (editingId === c.id) reset();
    load();
  };

  return (
    <>
      <h2 className="mb-1 text-body-emphasis">Saved replies</h2>
      <p className="text-body-tertiary mb-4">
        Reusable messages your team can drop into a chat. Type a shortcut like{' '}
        <code>/hours</code> in the composer to insert one.
      </p>

      <Row className="g-4">
        <Col xs={12} lg={5}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">{editingId ? 'Edit reply' : 'New reply'}</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fs-9">Title</Form.Label>
                <Form.Control
                  value={form.title}
                  placeholder="e.g. Business hours"
                  onChange={e => setForm({ ...form, title: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fs-9">Shortcut (optional)</Form.Label>
                <Form.Control
                  value={form.shortcut}
                  placeholder="/hours"
                  onChange={e => setForm({ ...form, shortcut: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fs-9">Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Add reply'}
                </Button>
                {editingId && (
                  <Button variant="phoenix-secondary" onClick={reset}>
                    Cancel
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={7}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">Your replies ({items.length})</h5>
              {items.length === 0 ? (
                <p className="fs-9 text-body-tertiary mb-0">No saved replies yet.</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {items.map(c => (
                    <div
                      key={c.id}
                      className="d-flex justify-content-between align-items-start border rounded p-2"
                    >
                      <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold">{c.title}</span>
                          {c.shortcut && (
                            <code className="fs-10 text-body-tertiary">{c.shortcut}</code>
                          )}
                        </div>
                        <div className="fs-9 text-body-tertiary line-clamp-2">{c.body}</div>
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0 ms-2">
                        <Button
                          variant="phoenix-secondary"
                          size="sm"
                          className="btn-icon"
                          onClick={() => edit(c)}
                          aria-label="Edit"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                        <Button
                          variant="phoenix-danger"
                          size="sm"
                          className="btn-icon"
                          onClick={() => remove(c)}
                          aria-label="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          show={!!toast}
          onClose={() => setToast(null)}
          delay={2500}
          autohide
          bg={toast?.ok ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default SavedReplies;
